import { Measurement } from '@core/components';
import {
  createIntermediateSamplingPoints,
  getClosestSegment,
  getPrelabeledWidth,
  SegmentWithWidth,
  toSegmentWithWidth,
  useKey,
} from '@core/logic';
import type { Vector } from '@core/types';
import {
  TransformationReason,
  useTransformationReasonDialogState,
} from '@processingResults/components/ProcessingResultsList';
import { processingResultSelectors } from '@processingResults/redux';
import { editModeState } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { checkboxSelectionState } from '@projects/pages/detail/menu/ScanOrganization/ScansInScopeList/checkboxSelectionState';
import { Html } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { useConnect } from '@scans/components/Scans/connect';
import { useScanIdsWithTexturedMeshInScope } from '@scans/components/Scans/hooks';
import { shouldShowManualPositionWarning } from '@scans/helper';
import { scanSelectors } from '@scans/redux';
import type { Scan } from '@scans/types';
import type { RootState } from '@core/redux/interface';
import { PointColors } from '@trenches/colors';
import {
  useFetchPrelabeledTrenches,
  usePrelabeledTrenchesState,
} from '@trenches/components/Prelabeled';
import type { MouseSphereMesh } from '@viewer3D/components';
import { setTransformControl } from '@viewer3D/components/Controls';
import {
  eulerToJson,
  getCachedMaterial,
  getRenderOrder,
  getVisibleScans,
  jsonToEuler,
  jsonToVector,
  vectorToJson,
} from '@viewer3D/helper';
import { sphereGeometry } from '@viewer3D/helper/geometry-cache';
import { Object3dNames } from '@viewer3D/types';
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { Event, Object3D } from 'three';
import { BufferGeometry, Euler, Quaternion, Vector3 } from 'three';
import { useSnapshot } from 'valtio';
import {
  setTriggerUndoUseUserTransformation,
  triggerUndoUseUserTransformation,
} from '../ScanDetailPanel/state';
import { TransformableScan } from '../TransformableScan';
import { setManualPositionWarningDialog } from '../ManualPositionWarningDialog';
import {
  createUndoTransformationRequestBody,
  getAxisAndAngleFromQuaternion,
  hasUserTransformationData,
} from './helper';
import { setTrenchFromPoint, trenchFromPointState } from './state';
import { useMeasurement } from './useMeasurement';

export interface RenderedScanData {
  scanId: string;
  position: Vector3;
  rotation: Euler;
  quaternion: Quaternion;
}

const lineGeometry = new BufferGeometry();
lineGeometry.setFromPoints([new Vector3(), new Vector3()]);

/**
 * This is a debug component in order to see the invisible sampling points on the segment the User is labeling.
 * All sampling points are rendered as spheres and show their respective calculated pre-labeled width as text.
 */
const SamplingPointSpheres: FC<{
  fromPoint?: Vector;
  toPoint: Vector;
  segmentsWithWidth: SegmentWithWidth[];
}> = ({ fromPoint, toPoint, segmentsWithWidth }) => {
  'use no memo';
  const { invalidate } = useThree();
  const [areSegmentEndsVisible, setAreSegmentEndsVisible] = useState(false);

  if (!fromPoint || segmentsWithWidth.length === 0) return null;

  const samplingPoints = createIntermediateSamplingPoints(fromPoint, toPoint);
  const closestSegments = samplingPoints.map((p) => getClosestSegment(p, segmentsWithWidth));

  const setSegmentEndsVisibility = (nextVisibility: boolean) => () => {
    setAreSegmentEndsVisible(nextVisibility);
    invalidate();
  };

  return (
    <>
      <mesh
        geometry={sphereGeometry}
        material={getCachedMaterial(PointColors.Endpoint, 0.5)}
        scale={[0.03, 0.03, 0.03]}
        position={[fromPoint.x, fromPoint.y, fromPoint.z]}
        renderOrder={getRenderOrder(Object3dNames.TrenchPoint)}
        visible={areSegmentEndsVisible}
      />
      <mesh
        geometry={sphereGeometry}
        material={getCachedMaterial(PointColors.Endpoint, 0.5)}
        scale={[0.03, 0.03, 0.03]}
        position={[toPoint.x, toPoint.y, toPoint.z]}
        renderOrder={getRenderOrder(Object3dNames.TrenchPoint)}
        visible={areSegmentEndsVisible}
      />
      {samplingPoints.map((p, i) => (
        <mesh
          key={i}
          geometry={sphereGeometry}
          material={getCachedMaterial('red', 0.5)}
          scale={[0.03, 0.03, 0.03]}
          position={[p.x, p.y, p.z]}
          renderOrder={getRenderOrder(Object3dNames.TrenchPoint)}
        >
          <Html>
            <div
              onMouseEnter={setSegmentEndsVisibility(true)}
              onMouseLeave={setSegmentEndsVisibility(false)}
            >
              {closestSegments[i][2]}
            </div>
          </Html>
        </mesh>
      ))}
    </>
  );
};

export const Scans = ({
  mouseSphereRef,
  scans,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  projectId,
}: {
  mouseSphereRef: MutableRefObject<MouseSphereMesh | null>;
  scans: Scan[]; // we use this to pass scans directly. Otherwise, we would have conflicts with the <ThreeContextBridge /> and url-query params in Html-components.
  projectId: string;
}) => {
  const {
    createTrench,
    createTrenchToExistingPoint,
    createTrenchFromExistingPoint,
    createTrenchBetweenExistingPoints,
    extendOrBranchTrench,
    extendOrBranchTrenchToPoint,
    resetScanToUserTransformation,
    resetUserTransformation,
    resolveSnapTarget,
    selectScansUnderMouseCursor,
    selectedPoint,
    selectedPointId,
    selectedScan,
    selectedTrenchId,
    transformScan,
    visibleScans,
    pointEntities,
  } = useConnect();
  const scanIds = useScanIdsWithTexturedMeshInScope(scans);
  const { scene, invalidate } = useThree((three) => three);
  const { mode: editMode } = useSnapshot(editModeState);
  const addPressed = useKey('a');
  const { fromPoint, fromPointId } = useSnapshot(trenchFromPointState);
  const [userTransformedScan, setUserTransformedScan] = useState<RenderedScanData | null>(null);
  const [hoverPoint, setHoverPoint] = useState<Vector3 | null>(null);
  const { trenches: prelabeledTrenches } = usePrelabeledTrenchesState();
  const { trigger } = useSnapshot(triggerUndoUseUserTransformation);
  const selectionSnapshot = useSnapshot(checkboxSelectionState);
  const { setTransformationReasonDialog } = useTransformationReasonDialogState();
  const store = useStore<RootState>();
  const processingResults = useSelector(processingResultSelectors.selectEntities);
  const selectedProcessingResult = useSelector(
    processingResultSelectors.selectById(selectedScan?.selectedProcessingResultId),
  );
  const confirmedWarningScanIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedScan?.id !== confirmedWarningScanIdRef.current) {
      confirmedWarningScanIdRef.current = null;
    }
  }, [selectedScan?.id]);

  useFetchPrelabeledTrenches(scanIds);

  useEffect(() => {
    invalidate();
  }, [visibleScans, invalidate]);

  // Reset scan transformation state when switching to a different scan
  useEffect(() => {
    if (userTransformedScan && selectedScan?.id !== userTransformedScan?.scanId) {
      // Don't hide transform controls here - the click handler will set them up for the new scan
      // Only reset the previous scan's transformation and clear the state
      resetScanToUserTransformation(userTransformedScan.scanId);
      setUserTransformedScan(null);
    }
    // Save button is pressed and all data is available to make the undo
    if (trigger && selectedScan?.selectedProcessingResultId && selectedProcessingResult) {
      const isResettingToAutoAlign =
        !selectedScan.disableAutoAlignedPosition && selectedScan.disableUserTransformation;
      const isResettingToOriginal =
        selectedScan.disableAutoAlignedPosition && selectedScan.disableUserTransformation;

      if (isResettingToAutoAlign || isResettingToOriginal) {
        if (userTransformedScan) {
          setUserTransformedScan(null);
        }

        const meshOffset = selectedProcessingResult.texturedMeshes?.[0]?.mesh?.offset;
        const reason = selectedProcessingResult.reason;

        let beforePosition: Vector3;
        let beforeRotation: Euler;

        if (reason === TransformationReason.AutoAligned) {
          beforePosition = jsonToVector(
            selectedProcessingResult.autoAlignmentTranslation || meshOffset || { x: 0, y: 0, z: 0 },
          );
          beforeRotation = jsonToEuler(selectedProcessingResult.autoAlignmentRotation);
        } else if (reason === TransformationReason.Unspecified) {
          // Edge case: UNSPECIFIED reason but user transformation data exists
          // Use user transformation data if available, otherwise use original position
          if (hasUserTransformationData(selectedProcessingResult)) {
            beforePosition = jsonToVector(
              selectedProcessingResult.userTranslation || meshOffset || { x: 0, y: 0, z: 0 },
            );
            beforeRotation = jsonToEuler(selectedProcessingResult.userRotation);
          } else {
            beforePosition = jsonToVector(meshOffset || { x: 0, y: 0, z: 0 });
            beforeRotation = new Euler();
          }
        } else {
          beforePosition = jsonToVector(
            selectedProcessingResult.userTranslation || meshOffset || { x: 0, y: 0, z: 0 },
          );
          beforeRotation = jsonToEuler(selectedProcessingResult.userRotation);
        }

        const beforeQuaternion = new Quaternion().setFromEuler(beforeRotation);

        const syntheticUserTransformedScan: RenderedScanData = {
          scanId: selectedScan.id,
          position: beforePosition,
          rotation: beforeRotation,
          quaternion: beforeQuaternion,
        };

        const body = createUndoTransformationRequestBody({
          scene,
          selectedScan,
          userTransformedScan: syntheticUserTransformedScan,
        });

        if (body) {
          const targetReason = isResettingToAutoAlign
            ? TransformationReason.AutoAligned
            : TransformationReason.Unspecified;

          const targetPosition = isResettingToAutoAlign
            ? jsonToVector(
                selectedProcessingResult.autoAlignmentTranslation ||
                  meshOffset || { x: 0, y: 0, z: 0 },
              )
            : jsonToVector(meshOffset || { x: 0, y: 0, z: 0 });

          const targetRotation = isResettingToAutoAlign
            ? jsonToEuler(selectedProcessingResult.autoAlignmentRotation)
            : new Euler();

          const targetQuaternion = new Quaternion().setFromEuler(targetRotation);
          const deltaQuaternion = targetQuaternion.multiply(beforeQuaternion.clone().invert());
          const { axis, angle } = getAxisAndAngleFromQuaternion(deltaQuaternion);

          const requestBody = {
            ...body,
            reason: targetReason,
            processingResult: {
              userTranslation: isResettingToAutoAlign
                ? selectedProcessingResult.autoAlignmentTranslation
                : vectorToJson(targetPosition),
              userRotation: isResettingToAutoAlign
                ? selectedProcessingResult.autoAlignmentRotation
                : eulerToJson(targetRotation),
            },
            ...(body.points && {
              points: {
                userTranslation: vectorToJson(targetPosition),
                userRotation: { angle, axis: vectorToJson(axis) },
                transformOrigin: vectorToJson(beforePosition),
              },
            }),
          };

          resetUserTransformation(
            selectedScan.id,
            selectedScan.selectedProcessingResultId,
            requestBody,
          );
        }
        setTriggerUndoUseUserTransformation(false);
      } else if (userTransformedScan) {
        const body = createUndoTransformationRequestBody({
          scene,
          selectedScan,
          userTransformedScan,
        });
        if (body) {
          const isApplyingToAutoAlign =
            !selectedScan.disableAutoAlignedPosition && selectedScan.disableUserTransformation;
          const targetReason = isApplyingToAutoAlign
            ? TransformationReason.AutoAligned
            : TransformationReason.Unspecified;

          resetUserTransformation(selectedScan.id, selectedScan.selectedProcessingResultId, {
            ...body,
            comment: '',
            reason: targetReason,
          });
        }
        setTriggerUndoUseUserTransformation(false);
        setUserTransformedScan(null);
      }
    }
  }, [trigger, selectedScan, selectedProcessingResult]);

  const measurement = useMeasurement({ invalidate });

  const hoverThrottleRef = useRef<{
    inThrottle: boolean;
    lastArgs: Vector3 | null;
    timeoutId: number | null;
  }>({
    inThrottle: false,
    lastArgs: null,
    timeoutId: null,
  });

  const throttledHoverUpdate = useMemo(() => {
    const run = (hp: Vector3) => {
      if (!mouseSphereRef.current) return;
      mouseSphereRef.current.position.copy(hp);
      setHoverPoint(hp.clone());
      invalidate();
    };

    const throttled = (hp: Vector3) => {
      const state = hoverThrottleRef.current;
      if (!state.inThrottle) {
        run(hp);
        state.inThrottle = true;
        state.timeoutId = window.setTimeout(() => {
          state.inThrottle = false;
          if (state.lastArgs) {
            const nextArgs = state.lastArgs;
            state.lastArgs = null;
            throttled(nextArgs);
          }
        }, 30);
      } else {
        state.lastArgs = hp;
      }
    };

    const cancel = () => {
      const state = hoverThrottleRef.current;
      if (state.timeoutId !== null) {
        window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
      }
      state.inThrottle = false;
      state.lastArgs = null;
    };

    return { throttled, cancel };
  }, [invalidate, mouseSphereRef]);

  useEffect(() => {
    return () => {
      throttledHoverUpdate.cancel();
    };
  }, [throttledHoverUpdate]);

  const addPointOverHandler = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();
      if (!event.intersections[0]) return;
      const { point: hp } = event.intersections[0];
      throttledHoverUpdate.throttled(hp);
    },
    [throttledHoverUpdate],
  );

  // TODO: just getting the prelabel via the fromPoint's processingResultId is a cheap implementation for now. S. https://deepup-gmbh.atlassian.net/browse/DP-3280?focusedCommentId=13432
  const prelabelForScan = useMemo(() => {
    let prId: string | undefined;
    if (fromPointId) {
      const scanId = pointEntities[fromPointId]?.scanId;
      prId = scans.find((s) => s.id === scanId)?.selectedProcessingResultId;
    }
    if (!prId) {
      prId = fromPoint?.processingResultId;
    }
    return !prId ? undefined : prelabeledTrenches.find((t) => t.processingResultId === prId);
  }, [fromPoint, fromPointId, pointEntities, prelabeledTrenches, scans]);

  const activeFromPoint = fromPoint ?? (fromPointId ? pointEntities[fromPointId] : undefined);

  const addPointClickHandler = useCallback(
    async ({ intersections, point, stopPropagation }: ThreeEvent<MouseEvent>) => {
      stopPropagation();
      if (!intersections.length) return;
      const [firstScan] = getVisibleScans(intersections);
      if (!firstScan) return;
      const newPoint = {
        ...point,
        scanId: firstScan.scanId,
        processingResultId: firstScan.processingResultId,
      };

      const snapTarget = resolveSnapTarget(newPoint, fromPointId);

      if (!selectedTrenchId && !fromPoint && !fromPointId) {
        setTrenchFromPoint(snapTarget ? undefined : newPoint, snapTarget?.id);
        return;
      }

      const currentFromPoint = fromPoint ?? (fromPointId ? pointEntities[fromPointId] : undefined);

      if (snapTarget) {
        const snappedWidth = getPrelabeledWidth(
          selectedPoint ?? currentFromPoint,
          snapTarget,
          toSegmentWithWidth(prelabelForScan),
        );

        if (fromPointId) {
          await createTrenchBetweenExistingPoints(
            fromPointId,
            snapTarget.id,
            prelabelForScan?.id,
            snappedWidth,
          );
          setTrenchFromPoint();
        } else if (fromPoint) {
          await createTrenchToExistingPoint(
            fromPoint,
            snapTarget.id,
            prelabelForScan?.id,
            snappedWidth,
          );
          setTrenchFromPoint();
        } else if (selectedPointId) {
          await extendOrBranchTrenchToPoint(snapTarget.id, prelabelForScan?.id, snappedWidth);
        }
        return;
      }

      const width = getPrelabeledWidth(
        selectedPoint ?? currentFromPoint,
        newPoint,
        toSegmentWithWidth(prelabelForScan),
      );

      if (fromPointId) {
        await createTrenchFromExistingPoint(fromPointId, newPoint, prelabelForScan?.id, width);
        setTrenchFromPoint();
      } else if (fromPoint) {
        await createTrench(fromPoint, newPoint, prelabelForScan?.id, width);
        setTrenchFromPoint();
      } else if (selectedPointId) {
        await extendOrBranchTrench(newPoint, prelabelForScan?.id, width);
      }
    },
    [
      selectedTrenchId,
      selectedPointId,
      createTrench,
      createTrenchToExistingPoint,
      createTrenchFromExistingPoint,
      createTrenchBetweenExistingPoints,
      extendOrBranchTrench,
      extendOrBranchTrenchToPoint,
      fromPoint,
      fromPointId,
      pointEntities,
      selectedPoint,
      prelabelForScan,
      resolveSnapTarget,
      setTrenchFromPoint,
    ],
  );
  const updateUserTransformedScan = async (e: Event) => {
    const object = (e.target as { object?: Object3D }).object;
    if (!object) return;

    // Position, rotation and quaternion need to be cloned before callback, otherwise they will have wrong values after the callback
    const position = object.position.clone();
    const rotation = object.rotation.clone();
    const quaternion = object.quaternion.clone();

    const transformCallback = transformScan(e);
    setTransformationReasonDialog(true, null, true, (reason, comment) => {
      if (transformCallback) {
        transformCallback(reason, comment);
        setUserTransformedScan({
          scanId: object.name.split(':')[1],
          position,
          rotation,
          quaternion,
        });
      }
    });
  };

  const selectScansClickHandler = useCallback(
    ({
      intersections,
      stopPropagation,
      nativeEvent,
      delta, // Distance between mouse down and mouse up event -> in three-js units, doesn't scale with zoom! Should prevent accidentally selecting scans when User wants to rotate with left mouse.
      object,
    }: ThreeEvent<MouseEvent>) => {
      stopPropagation();
      if (!intersections.length || delta > 1) return;
      const visibleScanIds = getVisibleScans(intersections).map((s) => s.scanId);
      if (!visibleScanIds.length) return;

      selectScansUnderMouseCursor(nativeEvent, visibleScanIds);

      if (object.parent) {
        // Store the scan data for potential transformation
        if (!userTransformedScan || userTransformedScan?.scanId !== visibleScanIds[0]) {
          setUserTransformedScan({
            scanId: visibleScanIds[0],
            position: object.parent.position.clone(),
            rotation: object.parent.rotation.clone(),
            quaternion: object.parent.quaternion.clone(),
          });
        }

        const scanId = visibleScanIds[0];

        const handleBeforeActivate = (mode: 'translate' | 'rotate', proceed: () => void) => {
          const state = store.getState();
          const scanToTransform =
            scanSelectors.selectById(scanId)(state) ??
            scans.find((s) => s.id === scanId) ??
            selectedScan;
          const processingResult = scanToTransform?.selectedProcessingResultId
            ? (processingResultSelectors.selectById(scanToTransform.selectedProcessingResultId)(
                state,
              ) ?? processingResults[scanToTransform.selectedProcessingResultId])
            : selectedProcessingResult;

          if (
            scanToTransform &&
            confirmedWarningScanIdRef.current !== scanToTransform.id &&
            shouldShowManualPositionWarning(scanToTransform, processingResult)
          ) {
            setManualPositionWarningDialog(
              true,
              () => {
                confirmedWarningScanIdRef.current = scanToTransform.id;
                proceed();
              },
              () => {
                // Cancelled - gizmo remains hidden
              },
            );
          } else {
            proceed();
          }
        };

        // Always set up transform controls for the selected scan
        // The TransformControl component handles visibility based on T/R key state
        setTransformControl({
          objectName: object.parent.name,
          onAfterTranslate: updateUserTransformedScan,
          onAfterRotate: updateUserTransformedScan,
          onBeforeActivate: handleBeforeActivate,
        });
      }
      invalidate();
    },
    [
      invalidate,
      selectScansUnderMouseCursor,
      transformScan,
      selectedScan,
      userTransformedScan,
      store,
      scans,
      processingResults,
      selectedProcessingResult,
    ],
  );

  useEffect(() => {
    if (!selectedScan) {
      setTransformControl();
    }
  }, [selectedScan]);

  useEffect(() => {
    return () => {
      setTransformControl();
    };
  }, []);

  const onOverHandler = measurement.isActive
    ? measurement.onOver
    : editMode === EditMode.Trench && addPressed
      ? addPointOverHandler
      : undefined;

  const clickHandler = measurement.isActive
    ? measurement.onClick
    : editMode === EditMode.Scan
      ? selectScansClickHandler
      : editMode === EditMode.Trench && addPressed
        ? addPointClickHandler
        : undefined;
  return (
    <>
      {measurement.isActive && (
        <Measurement divRef={measurement.divRef} groupRef={measurement.groupRef} />
      )}
      {activeFromPoint && (
        <mesh
          name={Object3dNames.TrenchPointFromIndicator}
          geometry={sphereGeometry}
          material={getCachedMaterial(PointColors.Endpoint, 0.5)}
          scale={[0.03, 0.03, 0.03]}
          position={[activeFromPoint.x, activeFromPoint.y, activeFromPoint.z]}
          renderOrder={getRenderOrder(Object3dNames.TrenchPoint)}
        />
      )}
      {window.debugUserTrenchSegment && hoverPoint && (activeFromPoint || selectedPoint) && (
        <SamplingPointSpheres
          fromPoint={activeFromPoint ?? selectedPoint}
          toPoint={hoverPoint}
          segmentsWithWidth={toSegmentWithWidth(prelabelForScan)}
        />
      )}
      <group name={Object3dNames.Scans} renderOrder={getRenderOrder(Object3dNames.Scans)}>
        {visibleScans !== 'NONE' &&
          scanIds.map((id) => (
            <TransformableScan
              key={id}
              scanId={id}
              visibleScans={visibleScans}
              onClick={clickHandler}
              onOver={onOverHandler}
              isScanChecked={selectionSnapshot.checkedScans.some((scan) => scan.id === id)}
            />
          ))}
      </group>
    </>
  );
};
